import React from 'react';
import { Globe } from 'lucide-react';
import type {
  RDAPInputSource,
  RDAPStepConfig as RDAPStepConfigType,
} from '../../types/rdap';
import type { Model, RegistryEnabledModel } from '../../types';
import { useProjectStore } from '../../store';
import { useModels } from '../../hooks/queries/useModels';
import { useQueue } from '../../hooks/useQueue';
import { apiService } from '../../services/api';

/**
 * Configuration form for the `rdap_lookup` orchestration step (AC-1434 / AC-1441 /
 * AC-1451).
 *
 * Rendered inside `StepConfigurationModal` when `stepType === 'rdap_lookup'`
 * (the wiring is owned by AC-1444 -- this file is just the panel itself).
 * Produces an `RDAPStepConfig` (see `frontend/src/types/rdap.ts`) stored
 * under `orchestration_step.config`.
 *
 * Layout:
 *   - input_source: radios for literal / upstream_list / upstream_scalar.
 *   - literal_domains: textarea visible only in 'literal' mode. One domain
 *     per line. Whitespace-only lines are ignored.
 *   - source_field: dropdown of dotted paths discovered by introspecting the
 *     upstream step's output_model (AC-1451). The user can also type a custom
 *     path via the "Advanced: enter custom path" toggle when none of the
 *     suggestions match.
 *   - (output_field removed in AC-1456 — the result is always RDAPLookupResponse-shaped)
 *   - global_concurrency: number input, 1..25, default 10.
 *   - per_server_concurrency: number input, 1..10, default 3.
 *
 * Path syntax for source_field (AC-1451):
 *   - "domains"                       flat list[str] field on the upstream
 *   - "candidate_domain"              flat str field (UPSTREAM_SCALAR)
 *   - "items[].domain"                list[X] -> X.domain per element
 *   - "items[].payload.domain_name"   queue_read shape: list[Item] -> Item.payload.domain_name
 *   - "results[].domain"              chained rdap_lookup output
 *
 * The same syntax is interpreted by `_walk_path` in
 * backend/ai/openai/orchestration/rdap_step_executor.py.
 *
 * Validation matches `RDAPStepConfig.model_validator` in
 * `backend/schemas/rdap.py` -- the JSDoc in `types/rdap.ts` enumerates the
 * rules. Errors are surfaced inline so authors get immediate feedback;
 * the backend remains the authoritative validator.
 *
 * Save mechanism: the panel does NOT save directly. Like FilterStepConfig,
 * it emits the merged config via `onChange`; the parent modal owns the
 * mutation/save flow. This keeps the panel free of React Query plumbing
 * and matches the existing config-panel contract.
 */

export interface RDAPStepConfigProps {
  /** Current (possibly partial) config, as loaded from the step. */
  config: Partial<RDAPStepConfigType>;
  /** Callback invoked whenever the user edits the config. */
  onChange: (config: RDAPStepConfigType) => void;
  /**
   * Upstream step. AC-1451: introspected to populate the source_field
   * dropdown with paths discovered on its output_model. Mirrors the
   * FilterStepConfig pattern.
   */
  previousStep?: any;
  /** Workflow id, threaded through for parity with other step config panels. */
  workflowId?: number;
  /**
   * Active project id. AC-1488: required to load registry-enabled models for
   * the release-on-unknown picker. When absent, the picker is rendered but
   * stays disabled (mirrors FilterStepConfig's `projectIdMissing` gating).
   */
  projectId?: number;
}

const MAX_LITERAL_DOMAINS = 50;
const GLOBAL_CONCURRENCY_MIN = 1;
const GLOBAL_CONCURRENCY_MAX = 25;
const PER_SERVER_CONCURRENCY_MIN = 1;
const PER_SERVER_CONCURRENCY_MAX = 10;
const DEFAULT_GLOBAL_CONCURRENCY = 10;
const DEFAULT_PER_SERVER_CONCURRENCY = 3;

// AC-1485 / AC-1488: per-domain retry budget bounds. Mirrors the
// `max_retries: int = Field(default=3, ge=1, le=5)` on the backend
// `RDAPStepConfig` schema. Keep these in sync if the backend bounds change.
const MAX_RETRIES_MIN = 1;
const MAX_RETRIES_MAX = 5;
const DEFAULT_MAX_RETRIES = 3;

/** Cap on path-introspection recursion to avoid runaway in cyclic schemas. */
const MAX_INTROSPECTION_DEPTH = 3;

const LIST_PREFIX = /^(?:list|List)\[(.+)\]$/;
const SCALAR_TYPES = new Set(['int', 'str', 'float', 'bool', 'Any']);

/**
 * Candidate dotted path discovered by introspecting an upstream output_model.
 * AC-1451: `leafType` is either `'str'` (UPSTREAM_SCALAR) or `'list[str]'`
 * (UPSTREAM_LIST). The dropdown filters by the active mode.
 */
export interface PathCandidate {
  path: string;
  leafType: 'str' | 'list[str]';
}

/**
 * Normalize a property value to its type string.
 *
 * Properties come in two shapes across the codebase:
 *   - flat string: "list[RDAPDomainResult]" (legacy InstructionAssistant)
 *   - rich dict:   { type: "list[RDAPDomainResult]", description: "..." }
 *                  (recommended-schema flow / AC-1450)
 *
 * Returns null if the value isn't one of the recognized shapes.
 */
function extractTypeString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && typeof (value as any).type === 'string') {
    return (value as any).type;
  }
  return null;
}

/**
 * Resolve a class name to its property record by checking, in order:
 *   1. `nested_models[className].properties` on the upstream output model.
 *   2. `models` from the project store.
 *
 * This mirrors how ConditionBuilder.flattenProperties resolves nested model
 * references.
 */
function resolveClassProperties(
  className: string,
  previousStep: any,
  models: Model[],
): Record<string, unknown> | null {
  const upstreamModel =
    previousStep?.agent?.output_model ?? previousStep?.output_model;
  const nested = upstreamModel?.nested_models?.[className]?.properties;
  if (nested && typeof nested === 'object') return nested as Record<string, unknown>;

  const found = models.find((m) => m.class_name === className);
  if (found?.properties) return found.properties as Record<string, unknown>;

  return null;
}

/**
 * Walk an output_model's properties and emit candidate dotted paths.
 *
 * Returns paths whose final leaf type is either `str` or `list[str]`. Paths
 * are dotted with `[]` markers indicating list traversal. Recursion is
 * capped at MAX_INTROSPECTION_DEPTH so an accidentally cyclic schema can't
 * spin the UI forever.
 *
 * Examples emitted for an upstream of shape
 *   {
 *     domains: list[str],
 *     items: list[Item],
 *     // Item: { payload: Payload, queue_id: int }
 *     // Payload: { domain_name: str }
 *   }
 * are: `domains` (list[str]), `items[].queue_id` (NOT emitted - int isn't a
 * candidate), `items[].payload.domain_name` (list[str]).
 */
export function getCandidatePaths(
  previousStep: any,
  models: Model[],
  target: 'list' | 'scalar',
): PathCandidate[] {
  const root: Record<string, unknown> | undefined =
    previousStep?.agent?.output_model?.properties ??
    previousStep?.output_model?.properties;
  if (!root || typeof root !== 'object') return [];

  const candidates: PathCandidate[] = [];

  const walk = (
    properties: Record<string, unknown>,
    prefix: string,
    depth: number,
    insideArray: boolean,
  ): void => {
    if (depth > MAX_INTROSPECTION_DEPTH) return;

    for (const [name, value] of Object.entries(properties)) {
      const fullPath = prefix ? `${prefix}.${name}` : name;
      const typeStr = extractTypeString(value);

      // ---- list[X] -------------------------------------------------------
      if (typeStr) {
        const listMatch = typeStr.match(LIST_PREFIX);
        if (listMatch) {
          const inner = listMatch[1].trim();
          if (SCALAR_TYPES.has(inner)) {
            // list[str] (or list[int]/list[bool], but only list[str] is a
            // candidate for the list target).
            if (inner === 'str') {
              candidates.push({ path: fullPath, leafType: 'list[str]' });
            }
            continue;
          }
          // list[ClassName] — once we descend, we're inside an array.
          // Element paths are recorded with `[]` after the field name.
          const arrayPath = `${fullPath}[]`;
          const nestedProps = resolveClassProperties(inner, previousStep, models);
          if (nestedProps) {
            walk(nestedProps, arrayPath, depth + 1, true);
          }
          continue;
        }

        if (typeStr === 'str') {
          if (insideArray) {
            // We're already pivoting; emit as list[str] candidate.
            candidates.push({ path: fullPath, leafType: 'list[str]' });
          } else {
            candidates.push({ path: fullPath, leafType: 'str' });
          }
          continue;
        }

        // Other scalar types (int/float/bool) are not RDAP candidates.
        if (SCALAR_TYPES.has(typeStr)) continue;

        // Unknown type string — it might be a class name. Try to resolve.
        const nestedProps = resolveClassProperties(typeStr, previousStep, models);
        if (nestedProps) {
          walk(nestedProps, fullPath, depth + 1, insideArray);
        }
        continue;
      }

      // ---- rich dict with inline `properties` ----------------------------
      if (
        value &&
        typeof value === 'object' &&
        (value as any).properties &&
        typeof (value as any).properties === 'object'
      ) {
        walk(
          (value as any).properties as Record<string, unknown>,
          fullPath,
          depth + 1,
          insideArray,
        );
        continue;
      }
    }
  };

  walk(root, '', 0, false);

  // De-duplicate (a property could be reachable through both nested_models
  // and the project store fallback) while preserving discovery order.
  const seen = new Set<string>();
  const unique: PathCandidate[] = [];
  for (const c of candidates) {
    const key = `${c.path}::${c.leafType}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(c);
  }

  if (target === 'list') {
    return unique.filter((c) => c.leafType === 'list[str]');
  }
  return unique.filter((c) => c.leafType === 'str');
}

/**
 * Parse a newline-separated domain textarea into an array of trimmed,
 * non-empty domains. Whitespace-only lines are dropped silently so the
 * domain count matches what the backend will receive.
 */
function parseDomainTextarea(value: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * Serialize literal_domains[] back into the textarea representation. We
 * keep one domain per line; trailing newline is omitted so the round-trip
 * (textarea -> array -> textarea) is stable.
 */
function serializeDomainsToTextarea(domains: string[]): string {
  return (domains || []).join('\n');
}

const RDAPStepConfig: React.FC<RDAPStepConfigProps> = ({
  config,
  onChange,
  previousStep,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  workflowId,
  projectId,
}) => {
  const inputSource: RDAPInputSource = config.input_source ?? 'upstream_list';
  const literalDomains = config.literal_domains ?? [];
  const sourceField = config.source_field ?? '';
  const globalConcurrency =
    config.global_concurrency ?? DEFAULT_GLOBAL_CONCURRENCY;
  const perServerConcurrency =
    config.per_server_concurrency ?? DEFAULT_PER_SERVER_CONCURRENCY;

  // AC-1485 / AC-1488: release-on-unknown configuration. Values fall back to
  // backend defaults when the saved config predates AC-1485 (so legacy steps
  // render with an explicit `3` in the retry box instead of a blank field).
  const releaseUnresolved = config.release_unresolved ?? false;
  const releaseRegistryModelId =
    config.release_registry_model_id === undefined
      ? null
      : config.release_registry_model_id;
  const maxRetries = config.max_retries ?? DEFAULT_MAX_RETRIES;

  // Project models for nested_models fallback lookup (AC-1451). When the
  // upstream output_model declares `list[ClassName]` but doesn't carry the
  // class definition under `nested_models`, we fall back to the project
  // store the same way ConditionBuilder.flattenProperties does.
  const currentProjectId = useProjectStore(
    (state) => state.currentProject?.project_id,
  );
  const modelsQuery = useModels(currentProjectId);
  const models = React.useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);

  // AC-1452: queue_read upstream steps don't carry an `output_model` shape,
  // so the introspection that powers the source-field dropdown finds nothing.
  // Synthesize a QueueReadOutput-shaped model from the queue's payload model
  // (looked up via `queue.data_model_id`) so the walker can descend
  // `items[].payload.<string-field>` paths the user actually wants to pick.
  const sourceQueueId =
    previousStep?.step_type === 'queue_read'
      ? (previousStep?.config?.source_queue_id as number | undefined)
      : undefined;
  const { queue } = useQueue(sourceQueueId);
  const introspectionTarget = React.useMemo(() => {
    if (previousStep?.step_type !== 'queue_read') return previousStep;
    // If the upstream already carries an output_model (test harnesses or a
    // future code path that populates it), trust it as-is.
    if (previousStep?.output_model?.properties) return previousStep;
    const payloadModel =
      queue?.data_model_id != null
        ? models.find((m) => m.model_id === queue.data_model_id)
        : undefined;
    // We always include the QueueItem shape so item_id / status / created_at
    // are still discoverable even when the queue's payload schema is unknown.
    const queueItemProperties: Record<string, string> = {
      item_id: 'str',
      status: 'str',
      created_at: 'str',
      // payload's type is the user-defined class name (resolved via project
      // models); when the payload model isn't loaded yet, fall back to a
      // permissive marker that the walker will treat as a non-descendable
      // leaf so we don't crash, just produce no candidates under payload.
      payload: payloadModel?.class_name ?? 'Any',
    };
    return {
      ...previousStep,
      output_model: {
        class_name: 'QueueReadOutput',
        properties: {
          items: 'list[__RDAPQueueItem__]',
          item_ids: 'list[str]',
        },
        nested_models: {
          __RDAPQueueItem__: { properties: queueItemProperties },
        },
      },
    };
  }, [previousStep, queue, models]);

  // Build the candidate list for the active mode. UPSTREAM_LIST shows
  // list[str]-leaf paths; UPSTREAM_SCALAR shows str-leaf paths.
  const candidates = React.useMemo<PathCandidate[]>(() => {
    if (inputSource === 'upstream_list') {
      return getCandidatePaths(introspectionTarget, models, 'list');
    }
    if (inputSource === 'upstream_scalar') {
      return getCandidatePaths(introspectionTarget, models, 'scalar');
    }
    return [];
  }, [introspectionTarget, models, inputSource]);

  // "Custom path" advanced toggle (AC-1451). Auto-enable when the saved
  // source_field doesn't appear in the candidate list (e.g. user typed it
  // manually before, or the upstream model changed). This way the user
  // sees their value reflected and can edit it freely.
  const sourceFieldInCandidates = candidates.some((c) => c.path === sourceField);
  const [customPathMode, setCustomPathMode] = React.useState<boolean>(
    () => !!sourceField && !sourceFieldInCandidates,
  );
  React.useEffect(() => {
    if (sourceField && !sourceFieldInCandidates && !customPathMode) {
      setCustomPathMode(true);
    }
    // Intentionally do NOT auto-flip back to dropdown when the user clears
    // the field — they explicitly toggled into custom mode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceField, sourceFieldInCandidates]);

  /**
   * Local mirror of the textarea text. We can't derive this purely from
   * `literalDomains` on every render because (a) blank lines mid-edit would
   * be eaten, and (b) the user expects their typing to survive between
   * keystrokes. We sync from props when the config-supplied list differs
   * from the parsed local text, which catches external replacements (step
   * reload after save) without clobbering in-progress edits.
   */
  const [textareaValue, setTextareaValue] = React.useState<string>(() =>
    serializeDomainsToTextarea(literalDomains),
  );
  const lastEmittedDomainsRef = React.useRef<string>(
    JSON.stringify(literalDomains),
  );

  React.useEffect(() => {
    const incoming = JSON.stringify(literalDomains);
    if (incoming !== lastEmittedDomainsRef.current) {
      lastEmittedDomainsRef.current = incoming;
      setTextareaValue(serializeDomainsToTextarea(literalDomains));
    }
  }, [literalDomains]);

  /**
   * Build the full config payload. Every emit returns a complete
   * RDAPStepConfig so the parent can persist it directly without merging.
   *
   * AC-1488: the three release-on-unknown fields participate in every emit so
   * users can flip between them (e.g. toggle the checkbox off and back on)
   * without losing the selected registry model.
   */
  const emit = (next: Partial<RDAPStepConfigType>): void => {
    const merged: RDAPStepConfigType = {
      input_source: next.input_source ?? inputSource,
      literal_domains: next.literal_domains ?? literalDomains,
      source_field:
        next.source_field !== undefined ? next.source_field : sourceField || undefined,
      global_concurrency: next.global_concurrency ?? globalConcurrency,
      per_server_concurrency:
        next.per_server_concurrency ?? perServerConcurrency,
      release_unresolved:
        next.release_unresolved !== undefined
          ? next.release_unresolved
          : releaseUnresolved,
      release_registry_model_id:
        next.release_registry_model_id !== undefined
          ? next.release_registry_model_id
          : releaseRegistryModelId,
      max_retries: next.max_retries ?? maxRetries,
    };
    onChange(merged);
  };

  const handleInputSourceChange = (value: RDAPInputSource): void => {
    emit({ input_source: value });
  };

  const handleDomainsTextareaChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ): void => {
    const raw = e.target.value;
    setTextareaValue(raw);
    const parsed = parseDomainTextarea(raw);
    lastEmittedDomainsRef.current = JSON.stringify(parsed);
    emit({ literal_domains: parsed });
  };

  const handleSourceFieldDropdownChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    emit({ source_field: e.target.value });
  };

  const handleSourceFieldTextChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    emit({ source_field: e.target.value });
  };

  const handleGlobalConcurrencyChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    // Coerce to number; empty input becomes NaN which will fail validation
    // and surface an inline error rather than silently emitting garbage.
    const raw = e.target.value;
    const parsed = raw === '' ? NaN : Number(raw);
    emit({ global_concurrency: parsed });
  };

  const handlePerServerConcurrencyChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const raw = e.target.value;
    const parsed = raw === '' ? NaN : Number(raw);
    emit({ per_server_concurrency: parsed });
  };

  // -----------------------------------------------------------------------
  // AC-1488: release-on-unknown handlers + lazy registry-model load.
  // -----------------------------------------------------------------------

  // Mirrors FilterStepConfig.tsx (AC-1358) -- fetch registry-enabled models
  // lazily, only after the user enables release_unresolved. This keeps
  // panels that never enable the feature from making an API call on mount.
  const [registryModels, setRegistryModels] = React.useState<RegistryEnabledModel[]>([]);
  const [registryModelsLoaded, setRegistryModelsLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!releaseUnresolved) return;
    if (projectId === undefined || projectId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const models = await apiService.getRegistryEnabledModels(projectId as number);
        if (!cancelled) {
          setRegistryModels(models || []);
          setRegistryModelsLoaded(true);
        }
      } catch {
        if (!cancelled) {
          setRegistryModels([]);
          setRegistryModelsLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [releaseUnresolved, projectId]);

  const handleReleaseUnresolvedChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    emit({ release_unresolved: e.target.checked });
  };

  const handleReleaseRegistryModelChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ): void => {
    // Empty string clears the selection back to null so the pairing-rule
    // validator (release_unresolved=true requires id) can fire.
    const raw = e.target.value;
    const next: number | null = raw === '' ? null : Number(raw);
    emit({ release_registry_model_id: next });
  };

  const handleMaxRetriesChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const raw = e.target.value;
    const parsed = raw === '' ? NaN : Number(raw);
    emit({ max_retries: parsed });
  };

  // -----------------------------------------------------------------------
  // Validation (mirrors the JSDoc on RDAPStepConfig in types/rdap.ts and the
  // backend `_validate` model_validator in backend/schemas/rdap.py).
  // -----------------------------------------------------------------------

  const literalDomainsCount = literalDomains.length;
  const literalEmptyError =
    inputSource === 'literal' && literalDomainsCount === 0;
  const sourceFieldEmptyError =
    inputSource !== 'literal' && (!sourceField || sourceField.trim() === '');
  const globalConcurrencyError =
    !Number.isFinite(globalConcurrency) ||
    globalConcurrency < GLOBAL_CONCURRENCY_MIN ||
    globalConcurrency > GLOBAL_CONCURRENCY_MAX;
  const perServerConcurrencyError =
    !Number.isFinite(perServerConcurrency) ||
    perServerConcurrency < PER_SERVER_CONCURRENCY_MIN ||
    perServerConcurrency > PER_SERVER_CONCURRENCY_MAX;
  const exceedsMaxLiteralDomains =
    inputSource === 'literal' && literalDomainsCount > MAX_LITERAL_DOMAINS;

  // AC-1488: pairing rule mirrors the backend `_validate` model_validator on
  // RDAPStepConfig -- `release_unresolved=true` requires a registry model.
  const releasePairingError =
    releaseUnresolved &&
    (releaseRegistryModelId === null || releaseRegistryModelId === undefined);
  const maxRetriesError =
    !Number.isFinite(maxRetries) ||
    maxRetries < MAX_RETRIES_MIN ||
    maxRetries > MAX_RETRIES_MAX;

  const projectIdMissing = projectId === undefined || projectId === null;
  const releasePickerValue =
    releaseRegistryModelId === null || releaseRegistryModelId === undefined
      ? ''
      : String(releaseRegistryModelId);

  const dropdownLeafTypeLabel =
    inputSource === 'upstream_list' ? 'list[str]' : 'str';

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
        <Globe className="w-4 h-4" />
        <span>Domain Availability (RDAP) step configuration</span>
      </div>

      {/* ----------------------------------------------------------------
          Input source toggle
          ---------------------------------------------------------------- */}
      <fieldset>
        <legend className="block text-sm font-medium mb-1">Input source</legend>
        <div className="flex flex-col gap-1">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="rdap-input-source"
              value="literal"
              aria-label="literal"
              checked={inputSource === 'literal'}
              onChange={() => handleInputSourceChange('literal')}
            />
            Literal list (type domains directly)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="rdap-input-source"
              value="upstream_list"
              aria-label="upstream list"
              checked={inputSource === 'upstream_list'}
              onChange={() => handleInputSourceChange('upstream_list')}
            />
            Upstream list (read a list field from the previous step's output)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="rdap-input-source"
              value="upstream_scalar"
              aria-label="upstream scalar"
              checked={inputSource === 'upstream_scalar'}
              onChange={() => handleInputSourceChange('upstream_scalar')}
            />
            Upstream scalar (read a single string field from the previous step)
          </label>
        </div>
      </fieldset>

      {/* ----------------------------------------------------------------
          Literal domains textarea
          ---------------------------------------------------------------- */}
      {inputSource === 'literal' && (
        <div>
          <label
            htmlFor="rdap-literal-domains"
            className="block text-sm font-medium mb-1"
          >
            Domain list
            <span className="text-red-600 ml-1">*</span>
          </label>
          <textarea
            id="rdap-literal-domains"
            aria-label="domain list"
            className="input w-full font-mono text-sm"
            rows={6}
            placeholder={'example.com\nmysite.net'}
            value={textareaValue}
            onChange={handleDomainsTextareaChange}
          />
          <p className="text-xs text-gray-500 mt-1">
            One domain per line. {literalDomainsCount} domain
            {literalDomainsCount === 1 ? '' : 's'} parsed.
          </p>
          {literalEmptyError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              At least one domain is required for literal input source.
            </p>
          )}
          {exceedsMaxLiteralDomains && (
            <p
              role="alert"
              className="text-xs text-amber-600 dark:text-amber-400 mt-1"
            >
              Domain list exceeds 50 entries (current: {literalDomainsCount}).
              The backend tool rejects more than 50 domains per call.
            </p>
          )}
        </div>
      )}

      {/* ----------------------------------------------------------------
          Source field input (upstream_* modes only)
          AC-1451: dropdown of dotted paths from upstream output model,
          with an Advanced toggle for free-form custom paths.
          ---------------------------------------------------------------- */}
      {inputSource !== 'literal' && (
        <div>
          <label
            htmlFor="rdap-source-field"
            className="block text-sm font-medium mb-1"
          >
            Source field
            <span className="text-red-600 ml-1">*</span>
          </label>

          {!customPathMode && (
            <>
              <select
                id="rdap-source-field"
                aria-label="source field"
                className="input w-full"
                value={sourceField}
                onChange={handleSourceFieldDropdownChange}
              >
                <option value="">
                  Select a {dropdownLeafTypeLabel} path on the upstream output...
                </option>
                {candidates.map((c) => (
                  <option key={c.path} value={c.path}>
                    {c.path} ({c.leafType})
                  </option>
                ))}
              </select>
              {candidates.length === 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  No {dropdownLeafTypeLabel} paths found on the upstream step's output model.
                </p>
              )}
            </>
          )}

          {customPathMode && (
            <input
              id="rdap-source-field"
              type="text"
              aria-label="source field"
              className="input w-full font-mono"
              placeholder={
                inputSource === 'upstream_list'
                  ? 'e.g. items[].payload.domain_name'
                  : 'e.g. result.candidate.domain'
              }
              value={sourceField}
              onChange={handleSourceFieldTextChange}
            />
          )}

          <div className="flex items-center justify-between mt-1">
            <p className="text-xs text-gray-500">
              {inputSource === 'upstream_list'
                ? 'Path to a list of domain strings on the upstream step output. Use [] to descend into a list, e.g. items[].payload.domain_name.'
                : 'Path to a single domain string on the upstream step output. Use dots for nested fields, e.g. result.candidate.domain.'}
            </p>
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline whitespace-nowrap ml-2"
              onClick={() => setCustomPathMode((v) => !v)}
              aria-label={
                customPathMode
                  ? 'use suggested paths'
                  : 'enter custom path'
              }
            >
              {customPathMode
                ? 'Use suggested paths'
                : 'Advanced: enter custom path'}
            </button>
          </div>

          {sourceFieldEmptyError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Source field is required for upstream input sources.
            </p>
          )}
        </div>
      )}

      {/* AC-1456: Output field removed. The step's output is always shaped
          like RDAPLookupResponse and persisted under data.results -- there
          is no per-step naming decision to surface to authors. */}

      {/* ----------------------------------------------------------------
          Concurrency controls
          ---------------------------------------------------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="rdap-global-concurrency"
            className="block text-sm font-medium mb-1"
          >
            Global concurrency
          </label>
          <input
            id="rdap-global-concurrency"
            type="number"
            aria-label="global concurrency"
            className="input w-full"
            min={GLOBAL_CONCURRENCY_MIN}
            max={GLOBAL_CONCURRENCY_MAX}
            value={Number.isFinite(globalConcurrency) ? globalConcurrency : ''}
            onChange={handleGlobalConcurrencyChange}
          />
          <p className="text-xs text-gray-500 mt-1">
            Total concurrent RDAP lookups across all servers. Range
            {' '}{GLOBAL_CONCURRENCY_MIN}..{GLOBAL_CONCURRENCY_MAX}, default
            {' '}{DEFAULT_GLOBAL_CONCURRENCY}.
          </p>
          {globalConcurrencyError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Global concurrency must be between {GLOBAL_CONCURRENCY_MIN} and
              {' '}{GLOBAL_CONCURRENCY_MAX}.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="rdap-per-server-concurrency"
            className="block text-sm font-medium mb-1"
          >
            Per-server concurrency
          </label>
          <input
            id="rdap-per-server-concurrency"
            type="number"
            aria-label="per server concurrency"
            className="input w-full"
            min={PER_SERVER_CONCURRENCY_MIN}
            max={PER_SERVER_CONCURRENCY_MAX}
            value={
              Number.isFinite(perServerConcurrency) ? perServerConcurrency : ''
            }
            onChange={handlePerServerConcurrencyChange}
          />
          <p className="text-xs text-gray-500 mt-1">
            Concurrent lookups per RDAP server (TLD). Range
            {' '}{PER_SERVER_CONCURRENCY_MIN}..{PER_SERVER_CONCURRENCY_MAX},
            default {DEFAULT_PER_SERVER_CONCURRENCY}. Keep low to respect
            rate limits.
          </p>
          {perServerConcurrencyError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Per-server concurrency must be between
              {' '}{PER_SERVER_CONCURRENCY_MIN} and
              {' '}{PER_SERVER_CONCURRENCY_MAX}.
            </p>
          )}
        </div>
      </div>

      {/* ----------------------------------------------------------------
          AC-1485 / AC-1488: Retry and release-on-unknown configuration.

          Two coordinated controls:
            1. max_retries: per-domain retry budget INSIDE RDAPService for
               transient failures (timeout, 5xx, 429). Always applies; doesn't
               require release_unresolved.
            2. release_unresolved + release_registry_model_id: opt-in cleanup
               of registry rows for domains that ended UNKNOWN after retry.
               Lets future runs re-attempt those domains.

          Picker pattern mirrors FilterStepConfig.tsx (lines 393-505) -- the
          picker is always rendered (so users see the affordance) but
          disabled until the checkbox is checked.
          ---------------------------------------------------------------- */}
      <div className="space-y-3 pt-2">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Retry &amp; release
        </div>

        <div>
          <label
            htmlFor="rdap-max-retries"
            className="block text-sm font-medium mb-1"
          >
            Max retries
          </label>
          <input
            id="rdap-max-retries"
            type="number"
            aria-label="max retries"
            className="input w-full"
            min={MAX_RETRIES_MIN}
            max={MAX_RETRIES_MAX}
            value={Number.isFinite(maxRetries) ? maxRetries : ''}
            onChange={handleMaxRetriesChange}
          />
          <p className="text-xs text-gray-500 mt-1">
            Per-domain retry budget for transient RDAP failures (timeout,
            network error, 5xx, 429). Range {MAX_RETRIES_MIN}..{MAX_RETRIES_MAX},
            default {DEFAULT_MAX_RETRIES}. 1 disables retries.
          </p>
          {maxRetriesError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">
              Max retries must be between {MAX_RETRIES_MIN} and {MAX_RETRIES_MAX}.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="rdap-release-unresolved"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <input
              id="rdap-release-unresolved"
              type="checkbox"
              aria-label="re-attempt unresolved domains in future runs"
              checked={releaseUnresolved}
              onChange={handleReleaseUnresolvedChange}
            />
            Re-attempt unresolved domains in future runs
          </label>
          <p className="text-xs text-gray-500 mt-1">
            When enabled, registry rows for domains that ended with status
            'unknown' after retry are deleted (scoped to this run), so future
            runs treat them as new and re-attempt the lookup.
          </p>
        </div>

        <div>
          <label
            htmlFor="rdap-release-registry-model"
            className="block text-sm font-medium mb-1"
          >
            Release registry model
          </label>
          <select
            id="rdap-release-registry-model"
            aria-label="release registry model"
            aria-describedby={
              releasePairingError ? 'rdap-release-pairing-error' : undefined
            }
            className="input w-full"
            value={releasePickerValue}
            disabled={!releaseUnresolved || projectIdMissing}
            onChange={handleReleaseRegistryModelChange}
          >
            <option value="">Select a registry-enabled model...</option>
            {registryModels.map((m) => (
              <option key={m.model_id} value={String(m.model_id)}>
                {m.class_name} ({m.entry_count} entries)
              </option>
            ))}
          </select>
          {releaseUnresolved && projectIdMissing && (
            <p className="text-xs text-gray-500 mt-1">
              Project required to load registry-enabled models. Select a project first.
            </p>
          )}
          {releaseUnresolved &&
            !projectIdMissing &&
            registryModelsLoaded &&
            registryModels.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No registry-enabled models in this project. Enable registry on a
                model first.
              </p>
            )}
          {releasePairingError && (
            <p
              id="rdap-release-pairing-error"
              role="alert"
              className="text-xs text-red-600 dark:text-red-400 mt-1"
            >
              Registry model is required when "Re-attempt unresolved domains"
              is enabled.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RDAPStepConfig;
